import React, { useEffect, useState, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { doc, getDoc, collection, getDocs, query, where, updateDoc } from "firebase/firestore";
import { db, realtimeDb } from "../firebaseConfig";
import { ref, get } from "firebase/database";
import WeeklySummaryCard from "./WeeklySummaryCard";
import ControlCard from "./ControlCard";
import ReferralProgram from "./ReferralProgram";
import InvoicesList from "./InvoicesList";
import { Alert, Modal, List, Badge, Typography } from "antd";
import { BellOutlined } from "@ant-design/icons";
import { motion } from "framer-motion";
import styled from "styled-components";
import lemonLogo from "../assets/logo.png";

/* -------------------------------------------------------------------------- */
/*                                   Styles                                   */
/* -------------------------------------------------------------------------- */

/**
 * 1) Container теперь занимает всю ширину (100vw) и высоту (100vh) экрана.
 * 2) Все дочерние элементы выстраиваются в колонку и центрируются по горизонтали.
 */
const Container = styled.div`

  padding: 0;         /* убираем лишние отступы */
`;

/**
 * При загрузке показываем крутящийся логотип по центру.
 */
const LoaderWrapper = styled.div`
  flex: 1; 
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`;

/**
 * 1) ContentWrap растягивается на всю ширину родителя.
 * 2) Если хотите ограничить максимально (например, на больших экранах), можно раскомментировать max-width.
 */
const ContentWrap = styled.div`
  width: 100%;          /* растягиваем карточки на всю ширину */

`;

/**
 * Обёртка для каждой карточки (если нужно добавить отступы между ними).
 */
const CardWrapper = styled(motion.div)`
  width: 100%;
  margin-bottom: 16px;  /* пространство между карточками */
`;

const Home = () => {
  const { currentUser } = useAuth();
  const [loading, setLoading] = useState(true);
  const [driverId, setDriverId] = useState(null);
  const [weekData, setWeekData] = useState({
    startDate: "N/A",
    endDate: "N/A",
    total: 0,
    status: "Loading...",
  });
  const [summaryData, setSummaryData] = useState({});
  const [appIncomes, setAppIncomes] = useState({});
  const [showPitAlert, setShowPitAlert] = useState(true);
  const [unreadNotifications, setUnreadNotifications] = useState([]);
  const [showNotifModal, setShowNotifModal] = useState(false);

  // Show unread notifications on login
  useEffect(() => {
    if (!currentUser) return;
    const loadUnread = async () => {
      try {
        const notifRef = collection(db, `users/${currentUser.uid}/notifications`);
        const q = query(notifRef, where('isRead', '==', false));
        const snap = await getDocs(q);
        if (!snap.empty) {
          const items = snap.docs.map(d => ({
            id: d.id,
            ...d.data(),
            createdAt: d.data().createdAt?.toDate?.() || new Date(),
          })).sort((a, b) => b.createdAt - a.createdAt);
          setUnreadNotifications(items);
          setShowNotifModal(true);
        }
      } catch (e) {
        console.error('Error loading notifications:', e);
      }
    };
    loadUnread();
  }, [currentUser]);

  const handleCloseNotifModal = async () => {
    setShowNotifModal(false);
    // Mark all as read
    try {
      for (const n of unreadNotifications) {
        await updateDoc(doc(db, `users/${currentUser.uid}/notifications`, n.id), {
          isRead: true,
          readAt: new Date(),
        });
      }
      setUnreadNotifications([]);
    } catch (e) {
      console.error('Error marking notifications:', e);
    }
  };

  /* ---------------------------- helpers ---------------------------- */
  const calculateNetto = useCallback((app) => {
    const fields = ["kursy", "przychod_dodatkowy", "commission"];
    return fields.reduce((s, f) => s + (Number(app[f]) || 0), 0);
  }, []);

  const calculateTotalIncome = useCallback(
    () =>
      Object.values(appIncomes).reduce((s, v) => s + v, 0) +
      (summaryData.bonusPartnera || 0) +
      (summaryData.vat_bonus || 0),
    [appIncomes, summaryData]
  );

  const calculateTotalExpenses = useCallback(() => {
    const g = (k) => summaryData[k] || 0;
    return (
      g("vat_dodatkowy") +
      g("vat_przejazdy") +
      g("wynajem") +
      g("partner") +
      g("zus") +
      g("inne")
    );
  }, [summaryData]);

  /* ---------------------------- fetch logic ---------------------------- */
  // 1. Get driverId from Firestore
  useEffect(() => {
    const fetchDriverId = async () => {
      if (!currentUser) return setLoading(false);
      try {
        const snap = await getDoc(doc(db, "users", currentUser.uid));
        if (!snap.exists()) return setLoading(false);
        const id = snap.data().driverId;
        setDriverId(id);
      } catch {
        setLoading(false);
      }
    };
    fetchDriverId();
  }, [currentUser]);

  // 2. Fetch latest completed week data
  useEffect(() => {
    if (!driverId) return;
    const fetchWeek = async () => {
      try {
        const wRef = ref(realtimeDb, `drivers/${driverId}/weeks`);
        const snap = await get(wRef);
        if (!snap.exists()) return setLoading(false);
        const weeksObj = snap.val();
        const sorted = Object.entries(weeksObj).sort(([a], [b]) => {
          const [aw, ay] = a.split("-").map(Number);
          const [bw, by] = b.split("-").map(Number);
          return by !== ay ? by - ay : bw - aw;
        });
        const latest = sorted.find(
          ([, w]) => w.summary?.status === "Rozliczenie zakończone"
        );
        if (!latest) return setLoading(false);

        const [key, data] = latest;
        setWeekData({
          startDate: data.summary?.startDate || "N/A",
          endDate: data.summary?.endDate || "N/A",
          total: data.summary?.total?.toFixed(2) || 0,
          status: data.summary?.status || "N/A",
        });
        setSummaryData(data.summary || {});

        const incomes = {};
        const apps = data.apps || {};
        Object.entries(apps).forEach(([appId, aData]) => {
          incomes[appId] = calculateNetto(aData);
        });
        setAppIncomes(incomes);
      } finally {
        setLoading(false);
      }
    };
    fetchWeek();
  }, [driverId, calculateNetto]);

  /* ---------------------------- animation variants ---------------------------- */
  const cardVariants = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { duration: 0.25, ease: "easeOut" } },
  };

  /* ---------------------------- render ---------------------------- */
  if (loading) {
    return (
      <Container>
        <LoaderWrapper>
          <motion.img
            src={lemonLogo}
            alt="Ładowanie"
            animate={{ rotate: 360 }}
            transition={{ repeat: Infinity, duration: 2, ease: "linear" }}
            style={{ width: 72, height: 72 }}
          />
        </LoaderWrapper>
      </Container>
    );
  }

  return (
    <Container>
      <ContentWrap>
        {showPitAlert && summaryData.isPitAlertRequired && (
          <Alert
            message="Przypomnienie PIT"
            description="Nie zapomnij złożyć PIT w terminie do końca miesiąca."
            type="warning"
            closable
            onClose={() => setShowPitAlert(false)}
            style={{ marginBottom: 16, width: "100%" }}
          />
        )}

        <CardWrapper variants={cardVariants} initial="hidden" animate="show">
          <WeeklySummaryCard
            weekData={weekData}
            summaryData={summaryData}
            appIncomes={appIncomes}
            calculateTotalIncome={calculateTotalIncome}
            calculateTotalExpenses={calculateTotalExpenses}
          />
        </CardWrapper>

        <CardWrapper variants={cardVariants} initial="hidden" animate="show">
          <ControlCard />
        </CardWrapper>

        <CardWrapper variants={cardVariants} initial="hidden" animate="show">
          <ReferralProgram />
        </CardWrapper>

        <CardWrapper variants={cardVariants} initial="hidden" animate="show">
          <InvoicesList />
        </CardWrapper>
      </ContentWrap>

      <Modal
        title={
          <span>
            <BellOutlined style={{ marginRight: 8, color: '#1890ff' }} />
            Masz {unreadNotifications.length} nieprzeczytanych powiadomień
          </span>
        }
        open={showNotifModal}
        onCancel={handleCloseNotifModal}
        onOk={handleCloseNotifModal}
        okText="Oznacz jako przeczytane"
        cancelText="Zamknij"
      >
        <List
          dataSource={unreadNotifications}
          renderItem={item => (
            <List.Item>
              <List.Item.Meta
                avatar={<Badge status="processing" />}
                title={item.title || 'Powiadomienie'}
                description={
                  <div>
                    <div>{item.text}</div>
                    <div style={{ fontSize: 11, color: '#999', marginTop: 4 }}>
                      {item.createdAt.toLocaleString('pl-PL')}
                    </div>
                  </div>
                }
              />
            </List.Item>
          )}
        />
      </Modal>
    </Container>
  );
};

export default Home;
